import React from 'react';
import type { AppraisalClassification } from '../../services/appraisalClassifier';

/**
 * AppraisalClassificationCard — visual rendering of the EPR × 360 bucket
 * with its recommended HR action. Surfaced on appraisal detail views so
 * HR sees the framework's guidance side-by-side with the raw scores —
 * this is the chokepoint that prevents bias from sliding into outcomes
 * unchallenged (e.g. Low/High patterns trigger 'manager bias' warning).
 */

const TONE: Record<AppraisalClassification['tone'], { card: string; icon: string; iconColor: string }> = {
  success: {
    card:      'border-emerald-200 bg-emerald-50',
    icon:      'workspace_premium',
    iconColor: 'text-emerald-700',
  },
  info: {
    card:      'border-blue-200 bg-blue-50',
    icon:      'verified',
    iconColor: 'text-blue-700',
  },
  warning: {
    card:      'border-amber-200 bg-amber-50',
    icon:      'warning',
    iconColor: 'text-amber-700',
  },
  error: {
    card:      'border-red-200 bg-red-50',
    icon:      'priority_high',
    iconColor: 'text-red-700',
  },
  neutral: {
    card:      'border-stone-200 bg-stone-50',
    icon:      'info',
    iconColor: 'text-stone-600',
  },
};

const AppraisalClassificationCard: React.FC<{
  classification: AppraisalClassification;
  eprScore?:  number | null;
  score360?:  number | null;
  compact?:   boolean;
}> = ({ classification: c, eprScore, score360, compact = false }) => {
  const t = TONE[c.tone];

  if (compact) {
    return (
      <span
        className={[
          'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium',
          t.card,
        ].join(' ')}
        title={`${c.meaning} — ${c.action}`}
      >
        <span className={`material-symbols-rounded ${t.iconColor}`} style={{ fontSize: 14 }}>
          {t.icon}
        </span>
        {c.label}
      </span>
    );
  }

  return (
    <section className={`rounded-xl border p-5 ${t.card}`}>
      <div className="flex items-start gap-3">
        <span
          className={`material-symbols-rounded ${t.iconColor} flex-shrink-0`}
          style={{ fontSize: 28 }}
          aria-hidden="true"
        >
          {t.icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline flex-wrap gap-2">
            <h3 className="text-base font-semibold text-stone-900">{c.label}</h3>
            <span className="text-xs text-stone-600">
              EPR <b>{fmt(eprScore)}</b> · 360 <b>{fmt(score360)}</b> · /5.00
            </span>
          </div>
          <p className="text-sm text-stone-800 mt-1">{c.meaning}</p>
          <div className="mt-3 pt-3 border-t border-current border-opacity-20">
            <div className="text-xs uppercase tracking-wide text-stone-600 font-medium mb-1">
              Recommended action
            </div>
            <p className="text-sm text-stone-900 font-medium">{c.action}</p>
          </div>
          {c.flagBias && (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-100 px-3 py-2 text-xs text-amber-900">
              <span className="font-semibold">⚠ Bias check:</span> The 360
              average ≥ 3.75 contradicts the manager's low EPR rating.
              HR should investigate whether the EPR scoring reflects
              actual delivery or a manager-specific lens.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

function fmt(n: number | undefined | null): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

export default AppraisalClassificationCard;
